import { useMemo } from 'react';
import type { HistoryResponse, WireIncident, WireService } from '@shared/contract';
import { STATUS_RANK } from '@shared/contract';
import { ServiceCard } from '@/components/ServiceCard';

export type StatusFilter = 'all' | 'down' | 'degraded' | 'ok';

interface Props {
  services: WireService[];
  openIncidents: WireIncident[];
  agents: unknown[];
  history: HistoryResponse['history'];
  loaded: boolean;
  filterText: string;
  onFilterTextChange: (value: string) => void;
  filterStatus: StatusFilter;
  onFilterStatusChange: (value: StatusFilter) => void;
  focusMode: boolean;
  onOpenService: (key: string) => void;
  searchRef: React.RefObject<HTMLInputElement | null>;
}

const STATUS_FILTERS: StatusFilter[] = ['all', 'down', 'degraded', 'ok'];
const FILTER_LABEL: Record<StatusFilter, string> = {
  all: 'All',
  down: 'Down',
  degraded: 'Degraded',
  ok: 'Healthy',
};

export function OverviewView({
  services,
  openIncidents,
  agents,
  history,
  loaded,
  filterText,
  onFilterTextChange,
  filterStatus,
  onFilterStatusChange,
  focusMode,
  onOpenService,
  searchRef,
}: Props) {
  const counts = useMemo(() => {
    const acc = { down: 0, degraded: 0, ok: 0 };
    for (const s of services) acc[s.status] += 1;
    return acc;
  }, [services]);

  const unacked = openIncidents.filter((i) => !i.ackedAt && !i.rootIncidentId).length;

  const visible = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    return services.filter((svc) => {
      if (focusMode && svc.status === 'ok') return false;
      if (filterStatus !== 'all' && svc.status !== filterStatus) return false;
      if (!needle) return true;
      return `${svc.name} ${svc.key} ${svc.group} ${svc.env}`.toLowerCase().includes(needle);
    });
  }, [services, filterText, filterStatus, focusMode]);

  // Unhealthy groups first, then unhealthy services within them. Nobody should
  // have to hunt a green grid for the one red tile.
  const groups = useMemo(() => {
    const map = new Map<string, WireService[]>();
    for (const svc of visible) {
      const list = map.get(svc.group);
      if (list) list.push(svc);
      else map.set(svc.group, [svc]);
    }
    const worstOf = (list: WireService[]) => Math.min(...list.map((s) => STATUS_RANK[s.status]));
    return [...map.entries()]
      .sort((a, b) => worstOf(a[1]) - worstOf(b[1]) || a[0].localeCompare(b[0]))
      .map(([name, list]) => ({
        name,
        services: [...list].sort(
          (a, b) =>
            STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
            a.tier - b.tier ||
            a.name.localeCompare(b.name)
        ),
      }));
  }, [visible]);

  return (
    <section className="view is-active" aria-label="Overview">
      <div className="stats">
        <Stat value={counts.down} label="Down" tone="down" />
        <Stat value={counts.degraded} label="Degraded" tone="degraded" />
        <Stat value={counts.ok} label="Healthy" tone="ok" />
        <Stat value={unacked} label="Unacked" />
        <Stat value={agents.length} label="Agents" />
      </div>

      <div className="toolbar">
        <div className="search">
          <svg viewBox="0 0 20 20" aria-hidden="true" className="search__icon">
            <path
              d="M13 13l4 4M8.5 15a6.5 6.5 0 110-13 6.5 6.5 0 010 13z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
          <input
            ref={searchRef}
            type="search"
            value={filterText}
            placeholder="Filter services…  ( / )"
            aria-label="Filter services by name, key, or group"
            autoComplete="off"
            onChange={(e) => onFilterTextChange(e.target.value)}
          />
        </div>
        <div className="segmented" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map((status) => (
            <button
              key={status}
              className={`seg${filterStatus === status ? ' is-active' : ''}`}
              aria-pressed={filterStatus === status}
              onClick={() => onFilterStatusChange(status)}
            >
              {FILTER_LABEL[status]}
            </button>
          ))}
        </div>
      </div>

      {!loaded ? (
        <div className="grid">
          {Array.from({ length: 6 }, (_, i) => (
            <div className="card card--skeleton" key={i}>
              <div className="skeleton" />
              <div className="skeleton" />
              <div className="skeleton" />
            </div>
          ))}
        </div>
      ) : !visible.length ? (
        <div className="empty">
          <strong>{services.length ? 'Nothing matches those filters' : 'No services registered'}</strong>
          {services.length
            ? 'Try clearing the search or switching back to “All”.'
            : 'Add entries under services: in config/services.yaml and restart.'}
        </div>
      ) : (
        groups.map((group) => {
          const unhealthy = group.services.filter((s) => s.status !== 'ok').length;
          return (
            <section className="group" key={group.name}>
              <div className="group__head">
                <h2 className="group__title">{group.name}</h2>
                {unhealthy > 0 && <span className="group__badge">{unhealthy} unhealthy</span>}
                <span className="group__rule" />
                <span className="muted group__count">{group.services.length}</span>
              </div>
              <div className="grid">
                {group.services.map((svc) => (
                  <ServiceCard
                    key={svc.key}
                    service={svc}
                    history={history}
                    onOpen={onOpenService}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}
    </section>
  );
}

function Stat({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone?: 'down' | 'degraded' | 'ok';
}) {
  const cls = value === 0 && tone !== 'ok' ? 'is-zero' : tone ? `is-${tone}` : '';
  return (
    <div className={`stat ${cls}`}>
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}
